"use client";

import React, { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import "./business-semantics.css";

/* Images — public/assets/images/ se serve hoti hain */
const IMG = "/assets/images";
const img = (name: string) => encodeURI(`${IMG}/${name}`);
const imgHero = img("business-semantics-hero.jpg");
const imgApproach = img("learn-collaborate 1.jpg");
const imgStory = img("Real-Business-Understanding.jpg");
const imgCta = img("lets-banner.jpg");

/* All styling lives in business-semantics.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--bs-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "people" | "gear" | "db" | "shield" | "nodes" | "stack" | "search" | "bolt"
  | "trend" | "factory" | "cart" | "bank" | "heart" | "check";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    people: (<><circle cx="9" cy="8" r="3" /><path d="M3 20c.4-3.6 2.6-5.4 6-5.4s5.6 1.8 6 5.4M16 5.5a3 3 0 0 1 0 5.8M18 14.8c1.8.6 2.8 2.3 3 5.2" /></>),
    gear: (<><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" /></>),
    db: (<><ellipse cx="12" cy="5.5" rx="7" ry="2.8" /><path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" /></>),
    shield: (<><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" /><path d="m9 12 2.2 2.2L15.5 10" /></>),
    nodes: (<><circle cx="12" cy="5" r="2.2" /><circle cx="5.5" cy="18" r="2.2" /><circle cx="18.5" cy="18" r="2.2" /><path d="m11 7 -4.5 9M13 7l4.5 9M8 18h8" /></>),
    stack: (<><path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3 12.5 9 5 9-5M3 16.5l9 5 9-5" /></>),
    search: (<><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></>),
    bolt: <path d="M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z" />,
    trend: (<><path d="M3 17l5-5 4 4 8-9" /><path d="M15 7h5v5" /></>),
    factory: <path d="M3 20V9l6 4V9l6 4V6h3v14H3ZM7 20v-3M11 20v-3M15 20v-3" />,
    cart: (<><circle cx="9" cy="19" r="1.6" /><circle cx="17" cy="19" r="1.6" /><path d="M3 4h2.5l2 11h10l2-8H7" /></>),
    bank: <path d="M3 9.5 12 4l9 5.5M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />,
    heart: <path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.5 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10Z" />,
    check: (<><circle cx="12" cy="12" r="8.5" /><path d="m8.5 12.2 2.4 2.4 4.6-5" /></>),
  };

  return (
    <svg
      className="bs-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* Partner logo: image agar file na mile to naam text me dikhta hai */
function PartnerLogo({ name, file }: { name: string; file: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span className="bs-partner-text">{name}</span>;
  return <img src={img(file)} alt={name} loading="lazy" onError={() => setFailed(true)} />;
}

/* ============================== DATA ============================== */

const partners = [
  { name: "SAP", file: "sap (1).jpg" },
  { name: "Salesforce", file: "salesforce.jpg" },
  { name: "Microsoft", file: "microsoft.jpg" },
  { name: "AWS", file: "aws.svg" },
  { name: "Google Cloud", file: "google-cloud.svg" },
];

const facets: { icon: IconKey; title: string; text: string }[] = [
  { icon: "people", title: "People", text: "Common business language" },
  { icon: "gear", title: "Process", text: "Consistent definitions" },
  { icon: "db", title: "Technology", text: "Connected data models" },
  { icon: "shield", title: "Governance", text: "Trusted and controlled meaning" },
];

const outcomes = [
  { big: "50%", text: "Faster report development" },
  { big: "Higher", text: "Data consistency across systems" },
  { big: "40%", text: "Reduction in business data queries" },
  { big: "Stronger", text: "Cross-functional collaboration" },
];

const layers = [
  { title: "Business Applications", note: "SAP, CRM, SFA, etc.", chips: ["SAP", "Salesforce", "CRM", "SFA"], accent: false },
  { title: "Semantic Layer", note: "The shared meaning", chips: ["Business terms", "Relationships", "Context"], accent: true },
  { title: "Enterprise Data", note: "Where the data lives", chips: ["Structured", "Unstructured", "Real-time"], accent: false },
];

const bridgePoints: { icon: IconKey; text: string }[] = [
  { icon: "stack", text: "Unified definitions across systems" },
  { icon: "search", text: "Business-friendly data access" },
  { icon: "bolt", text: "AI-ready data foundation" },
  { icon: "trend", text: "Faster and trusted decision-making" },
];

const approach = [
  { title: "Discover", lead: "Understand business processes, key terms and data landscape." },
  { title: "Design", lead: "Define business glossary, ontologies and data relationships." },
  { title: "Implement", lead: "Build semantic models, integrate with source systems and enable consumption." },
  { title: "Operationalise", lead: "Manage, govern and continuously evolve business semantics." },
];

const storyStats = [
  { big: "60%", text: "Faster report development" },
  { big: "35%", text: "Reduction in data reconciliation effort" },
  { big: "20+", text: "Markets harmonised" },
];

const otherCases = [
  "Enterprise data glossary",
  "Customer 360 definition",
  "Product hierarchy & taxonomy",
  "Sales and revenue definitions",
  "Cross-system data mapping",
  "AI/ML feature definitions",
];

const industries: { icon: IconKey; title: string }[] = [
  { icon: "factory", title: "Manufacturing" },
  { icon: "cart", title: "Retail & Consumer" },
  { icon: "bank", title: "Banking & Financial Services" },
  { icon: "bolt", title: "Energy & Utilities" },
  { icon: "heart", title: "Life Sciences & Healthcare" },
];

/* ============================== PAGE ============================== */

export default function BusinessSemanticsPage() {
  const ref = useScrollReveal();

  return (
    <div className="bs-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="bs-hero" id="top">
          <div className="bs-hero-bg" style={{ backgroundImage: `url("${imgHero}")` }} aria-hidden="true"></div>
          <div className="bs-hero-overlay" aria-hidden="true"></div>

          <div className="bs-hero-inner">
            <div className="bs-hero-copy">
              <h1 data-reveal style={delay(0)}>
                <span className="bs-line">A common language </span>
                <span className="bs-line">for <em>a smarter</em></span>
                <span className="bs-line"><em>enterprise.</em></span>
              </h1>
              <p data-reveal style={delay(1, 120)}>
                We unify business meaning across systems, data and people — so everyone works with
                consistent, trusted definitions and context.
              </p>
              <div className="bs-actions" data-reveal style={delay(2, 120)}>
                <Link href="/tellusyourproblem#tell-details" className="bs-btn bs-btn-primary">
                  Talk to Our Experts <span aria-hidden="true">→</span>
                </Link>
                <Link href="#use-cases" className="bs-btn bs-btn-outline">
                  Explore Use Cases <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= PARTNERS ============================ */}
        <section className="bs-partners" aria-label="Technology partners">
          <div className="bs-container">
            <div className="bs-partners-row" data-reveal>
              <p>Trusted by global organisations to unlock data value.</p>
              <ul>
                {partners.map((p) => (
                  <li key={p.name}><PartnerLogo name={p.name} file={p.file} /></li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ===================== SHARED UNDERSTANDING (facets) =============== */}
        <section className="bs-section" id="capabilities">
          <div className="bs-container">
            <div className="bs-shared">
              <div className="bs-shared-copy" data-reveal>
                <h2>Turn data into shared understanding.</h2>
                <p>
                  Our Business Semantics solutions define and manage business terms, relationships and context
                  across SAP, CRM and other enterprise systems — enabling consistent reporting, AI readiness and
                  better decision-making.
                </p>
                {/* <Link href="/solutions/business-semantics/capabilities" className="bs-link bs-link--lg">
                  Explore our capabilities <span aria-hidden="true">→</span>
                </Link> */}
              </div>

              <div className="bs-facets" data-reveal style={delay(1, 100)}>
                {facets.map((f) => (
                  <div className="bs-facet" key={f.title}>
                    <span className="bs-facet-icon"><Icon name={f.icon} /></span>
                    <div>
                      <strong>{f.title}</strong>
                      <span>{f.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============================= OUTCOMES ============================ */}
        <section className="bs-outcomes" aria-label="Business outcomes">
          <div className="bs-container">
            <div className="bs-outcomes-head" data-reveal>
              <h2>Business outcomes</h2>
            </div>
            <ul className="bs-outcomes-grid">
              {outcomes.map((o, i) => (
                <li key={o.text} data-reveal style={delay(i, 80)}>
                  <strong>{o.big}</strong>
                  <span>{o.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ========================== BRIDGE (layer stack) =================== */}
        <section className="bs-section">
          <div className="bs-container">
            <div className="bs-bridge">
              <div className="bs-bridge-copy" data-reveal>
                <h2>Bridge business, data and AI with semantic clarity.</h2>
                <p>
                  We build and operationalise business semantics using industry ontologies, semantic layers and
                  metadata management — helping you create a single source of business truth.
                </p>
                <ul className="bs-points">
                  {bridgePoints.map((b) => (
                    <li key={b.text}>
                      <span className="bs-point-icon"><Icon name={b.icon} /></span>
                      <span>{b.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bs-stack" data-reveal style={delay(1, 100)}>
                {layers.map((l) => (
                  <div className={`bs-layer${l.accent ? " is-accent" : ""}`} key={l.title}>
                    <div className="bs-layer-title">
                      <strong>{l.title}</strong>
                      <span>{l.note}</span>
                    </div>
                    <ul>
                      {l.chips.map((c) => <li key={c}>{c}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============================ APPROACH ============================= */}
        <section className="bs-section">
          <div className="bs-container">
            <div className="bs-approach">
              <div className="bs-approach-image" data-reveal>
                <img src={imgApproach} alt="" aria-hidden="true" loading="lazy" />
              </div>

              <div className="bs-approach-body">
                <h2 data-reveal>Our approach</h2>
                <ol>
                  {approach.map((a, i) => (
                    <li className="bs-approach-row" key={a.title} data-reveal style={delay(i, 80)}>
                      <span className="bs-approach-no">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <h3>{a.title}</h3>
                        <p>{a.lead}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ CUSTOMER STORY ======================= */}
        <section className="bs-story-section" id="use-cases">
          <div className="bs-container">
            <div className="bs-story">
              <div className="bs-story-image" data-reveal>
                <img src={imgStory} alt="" aria-hidden="true" loading="lazy" />
              </div>

              <div className="bs-story-body" data-reveal style={delay(1, 100)}>
                <h2>Unified business meaning across 20+ markets.</h2>
                <p>
                  Orane helped a global consumer goods company standardise key business terms across SAP and CRM,
                  enabling consistent reporting, faster insights and AI readiness.
                </p>
                {/* <Link href="/customers/business-semantics" className="bs-link">
                  Read the full story <span aria-hidden="true">→</span>
                </Link> */}

                <ul className="bs-story-stats">
                  {storyStats.map((s) => (
                    <li key={s.text}>
                      <strong>{s.big}</strong>
                      <span>{s.text}</span>
                    </li>
                  ))}
                </ul>

                <h3>Other use cases</h3>
                <ul className="bs-cases">
                  {otherCases.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= INDUSTRIES ========================== */}
        <section className="bs-section">
          <div className="bs-container">
            <div className="bs-head bs-head--row" data-reveal>
              <h2>Industries we serve</h2>
              {/* <Link href="/industries" className="bs-link">Explore all industries <span aria-hidden="true">→</span></Link> */}
            </div>

            <ul className="bs-industries">
              {industries.map((x, i) => (
                <li key={x.title} data-reveal style={delay(i, 70)}>
                  <span className="bs-industry-icon"><Icon name={x.icon} /></span>
                  <span>{x.title}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============================ FINAL CTA ============================ */}
        <section className="bs-cta" id="contact">
          <img className="bs-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="bs-cta-copy">
            <span className="bs-cta-eyebrow" data-reveal>BUILD A COMMON LANGUAGE FOR A SMARTER ENTERPRISE.</span>
            <h2 data-reveal style={delay(1, 120)}>Let&apos;s make your data speak the same language.</h2>
            <div className="bs-actions" data-reveal style={delay(2, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="bs-btn bs-btn-primary">
                Talk to Our Experts <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}